import { useEffect, useRef } from "react";

/**
 * Calls `handler` when a pointer press lands outside the returned ref's element.
 *
 * Listens on `mousedown` rather than `click` so a dropdown closes as the press
 * starts, matching how the Radix popovers elsewhere in the admin behave. `click`
 * would fire after any re-render the press triggers, which lets a list linger
 * for a frame or swallow the click entirely.
 *
 * Pass `enabled: false` while the element is hidden so idle components don't
 * each keep a document listener.
 */
export function useClickOutside(handler, enabled = true) {
    const ref = useRef(null);
    // Kept in a ref so an inline arrow handler doesn't re-bind the listener on
    // every render.
    const saved = useRef(handler);
    saved.current = handler;

    useEffect(() => {
        if (!enabled) return undefined;

        const onPointerDown = (event) => {
            const el = ref.current;
            if (el && !el.contains(event.target)) saved.current(event);
        };

        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("touchstart", onPointerDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("touchstart", onPointerDown);
        };
    }, [enabled]);

    return ref;
}
